'use client';

import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { env } from '@/env/env';
import { useAuth } from '../providers/AuthProvider';
import googleLogo from '@Assets/icons/google-logo.svg';

interface GoogleSignInButtonProps {
    className?: string;
    disabled?: boolean;
}

export const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
    className = '',
    disabled = false,
}) => {
    const { login } = useAuth();
    const callbackRef = useRef(login);
    // Keep the latest login in the ref via an effect: refs must not be written during render
    useEffect(() => {
        callbackRef.current = login;
    }, [login]);

    const widgetRef = useRef<HTMLDivElement>(null);
    const [widgetFailed, setWidgetFailed] = useState(false);

    useEffect(() => {
        const clientId = env('NEXT_PUBLIC_GOOGLE_CLIENT_ID');
        if (!clientId) {
            setWidgetFailed(true);
            return;
        }

        const initialize = () => {
            if (!widgetRef.current) return;

            window.google!.accounts.id.initialize({
                client_id: clientId,
                callback: (response: { credential?: string }) => {
                    if (response.credential) {
                        callbackRef.current(response.credential);
                    }
                },
            });

            widgetRef.current.innerHTML = '';
            window.google!.accounts.id.renderButton(widgetRef.current, {
                type: 'icon',
                size: 'large',
                shape: 'rectangular',
            });

            // Scale Google's iframe to fill the container so the entire button is clickable
            const stretchWidget = () => {
                const container = widgetRef.current;
                if (!container) return;
                const iframe = container.querySelector<HTMLIFrameElement>('iframe');
                if (!iframe) return;

                const cW = container.offsetWidth;
                const cH = container.offsetHeight;
                const iW = iframe.offsetWidth || 40;
                const iH = iframe.offsetHeight || 40;

                const wrapper = iframe.parentElement;
                if (wrapper && wrapper !== container) {
                    wrapper.style.width = `${cW}px`;
                    wrapper.style.height = `${cH}px`;
                    wrapper.style.overflow = 'hidden';
                }

                iframe.style.transformOrigin = 'top left';
                iframe.style.transform = `scale(${cW / iW}, ${cH / iH})`;
            };

            setTimeout(stretchWidget, 300);
        };

        if (window.google?.accounts?.id) {
            initialize();
            return;
        }

        const existingScript = document.getElementById('google-identity-script');
        if (existingScript) {
            existingScript.addEventListener('load', initialize);
            return () => existingScript.removeEventListener('load', initialize);
        }

        const script = document.createElement('script');
        script.id = 'google-identity-script';
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => setTimeout(initialize, 100);
        script.onerror = () => setWidgetFailed(true);
        document.head.appendChild(script);
    }, []);

    if (widgetFailed) return null;

    return (
        <div
            className={className}
            style={{ position: 'relative', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1 }}
        >
            <Image src={googleLogo} width={24} height={24} alt="Google" style={{ pointerEvents: 'none' }} />
            {/* Widget overlay: opacity >= 0.1 for widget's isVisible(), filter hides visually */}
            <div
                ref={widgetRef}
                style={{
                    position: 'absolute',
                    inset: 0,
                    overflow: 'hidden',
                    opacity: 1,
                    filter: 'opacity(0)',
                    pointerEvents: disabled ? 'none' : 'auto',
                }}
            />
        </div>
    );
};

export default GoogleSignInButton;
